import React, { useState, useEffect, useRef } from 'react';
import { User, Mail, Lock, Eye, EyeOff, Shield, AlertCircle, Upload } from 'lucide-react';
import { getAlternateEmailValidationError } from '../utils/emailValidation';
import Toast from './client/Toast';
import LoadingSpinner from './common/LoadingSpinner';

interface ProfileData {
  fullName: string;
  email: string;
  photoURL: string;
  alternateEmail: string;
}

interface PasswordData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface ShowPasswords {
  current: boolean;
  new: boolean;
  confirm: boolean;
}

interface ToastState {
  isVisible: boolean;
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
}

interface ProfileProps {
  onBack: () => void;
  user: { email?: string | null; displayName?: string | null; photoURL?: string | null; providerData?: Array<{ providerId: string }> } | null;
}

const Profile: React.FC<ProfileProps> = ({ user }) => {
  const [profileData, setProfileData] = useState<ProfileData>({
    fullName: '',
    email: '',
    photoURL: '',
    alternateEmail: ''
  });
  const [originalData, setOriginalData] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [selectedPhotoFile, setSelectedPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Password change states
  const [passwordData, setPasswordData] = useState<PasswordData>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState<ShowPasswords>({
    current: false,
    new: false,
    confirm: false
  });
  const [passwordError, setPasswordError] = useState<string>('');
  const [changingPassword, setChangingPassword] = useState<boolean>(false);
  const [alternateEmailError, setAlternateEmailError] = useState<string>('');

  // Toast notification state
  const [toast, setToast] = useState<ToastState>({
    isVisible: false,
    message: '',
    type: 'success'
  });

  const isEmailPasswordUser = true;

  // Toast helper functions
  const showToast = (message: string, type: 'success' | 'error' | 'info' | 'warning' = 'success') => {
    setToast({
      isVisible: true,
      message,
      type
    });
  };

  const hideToast = () => {
    setToast(prev => ({
      ...prev,
      isVisible: false
    }));
  };

  // Load user profile data from backend API
  useEffect(() => {
    const loadUserProfile = async () => {
      if (!user) return;

      try {
        setLoading(true);

        const token = localStorage.getItem('authToken');
        const apiBase = (typeof window !== 'undefined' && window.location.hostname === 'localhost')
          ? 'http://localhost:8000/api/'
          : (import.meta.env.VITE_API_URL || 'https://pawtected-client-dev-b941ac1d80e9.herokuapp.com/api/');

        const response = await fetch(`${apiBase}users/profile`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        let userData: any = {};
        if (response.ok) {
          userData = await response.json();
        }

        const fullName = userData.name || userData.displayName || user.displayName || '';

        const initialData: ProfileData = {
          fullName,
          email: userData.email || user.email || '',
          photoURL: userData.photoURL || user.photoURL || '',
          alternateEmail: userData.alternate_email || ''
        };

        setProfileData(initialData);
        setOriginalData(initialData);
      } catch (error) {
        console.error('Error loading user profile:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUserProfile();
  }, [user]);

  // Save profile data
  const handleSave = async () => {
    if (!user) return;

    // Validate alternate email
    const alternateEmailValidationError = getAlternateEmailValidationError(profileData.alternateEmail, profileData.email);
    if (alternateEmailValidationError) {
      setAlternateEmailError(alternateEmailValidationError);
      showToast(alternateEmailValidationError, 'error');
      return;
    }

    try {
      setSaving(true);

      const token = localStorage.getItem('authToken');
      const apiBase = (typeof window !== 'undefined' && window.location.hostname === 'localhost')
        ? 'http://localhost:8000/api/'
        : (import.meta.env.VITE_API_URL || 'https://pawtected-client-dev-b941ac1d80e9.herokuapp.com/api/');

      // Upload photo if selected
      if (selectedPhotoFile) {
        const formData = new FormData();
        formData.append('file', selectedPhotoFile);

        const photoResponse = await fetch(`${apiBase}users/profile/photo`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        if (!photoResponse.ok) {
          const errorData = await photoResponse.json();
          throw new Error(errorData.detail || 'Failed to upload photo');
        }

        const photoData = await photoResponse.json();
        setProfileData(prev => ({ ...prev, photoURL: photoData.photo_url }));
        setSelectedPhotoFile(null);
        setPhotoPreview(null);
      }

      // Update profile data (alternate email)
      const response = await fetch(`${apiBase}users/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: profileData.fullName,
          alternate_email: profileData.alternateEmail
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update profile');
      }

      setOriginalData({ ...profileData });

      console.log('Profile updated successfully');
      showToast('Profile updated successfully!', 'success');
    } catch (error: any) {
      console.error('Error saving profile:', error);
      showToast(error.message || 'Failed to save profile. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Handle input changes
  const handleInputChange = (field: keyof ProfileData, value: string) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));

    if (field === 'alternateEmail') {
      const error = getAlternateEmailValidationError(value, profileData.email);
      setAlternateEmailError(error);
    }
  };

  // Handle password change
  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');

    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      const errorMessage = 'Please fill in all password fields';
      setPasswordError(errorMessage);
      showToast(errorMessage, 'error');
      return;
    }

    if (passwordData.newPassword.length < 8) {
      const errorMessage = 'New password must be at least 8 characters';
      setPasswordError(errorMessage);
      showToast(errorMessage, 'error');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      const errorMessage = 'New passwords do not match';
      setPasswordError(errorMessage);
      showToast(errorMessage, 'error');
      return;
    }

    if (passwordData.currentPassword === passwordData.newPassword) {
      const errorMessage = 'New password must be different from current password';
      setPasswordError(errorMessage);
      showToast(errorMessage, 'error');
      return;
    }

    setChangingPassword(true);

    try {
      const token = localStorage.getItem('authToken');
      const apiBase = (typeof window !== 'undefined' && window.location.hostname === 'localhost')
        ? 'http://localhost:8000/api/'
        : (import.meta.env.VITE_API_URL || 'https://pawtected-client-dev-b941ac1d80e9.herokuapp.com/api/');

      const response = await fetch(`${apiBase}auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          current_password: passwordData.currentPassword,
          new_password: passwordData.newPassword
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to change password');
      }

      showToast('Password updated successfully!', 'success');

      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });

    } catch (error: any) {
      console.error('Password change error:', error);
      const errorMessage = error.message || 'Failed to change password';
      setPasswordError(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      setChangingPassword(false);
    }
  };

  const handlePasswordInputChange = (field: keyof PasswordData, value: string) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
    setPasswordError('');
  };

  const togglePasswordVisibility = (field: keyof ShowPasswords) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  // Handle photo file selection
  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      showToast('Invalid file type. Please use JPEG, PNG, GIF, or WebP.', 'error');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      showToast('File too large. Maximum size is 5MB.', 'error');
      return;
    }

    setSelectedPhotoFile(file);

    // Create preview URL
    const reader = new FileReader();
    reader.onloadend = () => {
      setPhotoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  // Check if there are unsaved changes
  const hasUnsavedChanges = (): boolean => {
    if (!originalData) return false;

    return (
      profileData.alternateEmail.trim() !== originalData.alternateEmail.trim() ||
      selectedPhotoFile !== null
    );
  };

  const hasChanges = hasUnsavedChanges();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="medium" text="Loading profile..." />
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex flex-col overflow-hidden">
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />

      {/* Header */}
      <div className="bg-white border-b border-gray-200 h-[73px] flex items-center flex-shrink-0">
        <div className="px-4 sm:px-5 w-full flex items-center justify-between">
          <h1 className="text-lg sm:text-xl font-semibold text-gray-900">Profile Settings</h1>
        </div>
      </div>

      {/* Content - Scrollable Area */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0">
        <div className="min-h-full py-6 sm:py-8">
          <div className="max-w-2xl mx-auto px-4 sm:px-5 space-y-6 sm:space-y-8 pb-20 sm:pb-32">

          {/* Profile Information Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 sm:mb-6 flex items-center gap-2">
              <User className="w-5 h-5 text-purple-600" />
              Profile Information
            </h2>
            <div className="space-y-4 sm:space-y-5">
              {/* Profile Photo */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-900">
                  Profile Photo
                </label>
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 rounded-full overflow-hidden bg-gray-100 border-2 border-gray-200 flex items-center justify-center">
                    {photoPreview || profileData.photoURL ? (
                      <img
                        src={photoPreview || profileData.photoURL}
                        alt="Profile"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <User className="w-10 h-10 text-gray-400" />
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handlePhotoSelect}
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors"
                    >
                      <Upload className="w-4 h-4" />
                      {profileData.photoURL || photoPreview ? 'Change Photo' : 'Upload Photo'}
                    </button>
                    <p className="text-xs text-gray-500">
                      JPEG, PNG, GIF, or WebP. Max 5MB.
                    </p>
                  </div>
                </div>
              </div>

              {/* Full Name */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-600">
                  Full Name
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={profileData.fullName}
                    disabled
                    className="w-full px-4 py-3 bg-gray-100 border border-gray-300 rounded-lg text-gray-500 cursor-not-allowed"
                    placeholder="Full Name"
                  />
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <User className="w-5 h-5 text-gray-400" />
                  </div>
                </div>
                <p className="text-xs text-gray-500">
                  Name cannot be changed
                </p>
              </div>

              {/* Email */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-600">
                  Email
                </label>
                <input
                  type="email"
                  value={profileData.email}
                  disabled
                  className="w-full px-4 py-3 bg-gray-100 border border-gray-300 rounded-lg text-gray-500 cursor-not-allowed"
                  placeholder="email@example.com"
                />
                <p className="text-xs text-gray-500">
                  Email cannot be changed
                </p>
              </div>

              {/* Alternate Email */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-900">
                  Alternate Email
                </label>
                <div className="relative">
                  <input
                    type="email"
                    value={profileData.alternateEmail}
                    onChange={(e) => handleInputChange('alternateEmail', e.target.value)}
                    className={`w-full px-4 py-3 bg-white border rounded-lg text-gray-900 focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                      alternateEmailError ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="alternate@example.com"
                  />
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <Mail className="w-5 h-5 text-gray-400" />
                  </div>
                </div>
                {alternateEmailError && (
                  <p className="text-xs text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {alternateEmailError}
                  </p>
                )}
                <p className="text-xs text-gray-500">
                  Optional: Add an alternate email for notifications (must be different from your primary email)
                </p>
              </div>

              {/* Save Button for Alternate Email */}
              <button
                onClick={handleSave}
                disabled={saving || !hasChanges}
                className={`w-full px-6 py-3 text-white rounded-full font-bold text-sm uppercase tracking-wide shadow-sm transition-all flex items-center justify-center gap-2 ${
                  saving || !hasChanges
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:shadow-md'
                }`}
                style={{ background: 'var(--pmc-primary)' }}
              >
                {saving ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Saving...</span>
                  </>
                ) : (
                  <span>Save</span>
                )}
              </button>
            </div>
          </div>

          {/* Password Change Section */}
          {isEmailPasswordUser && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <Shield className="w-5 h-5 text-purple-600" />
                Change Password
              </h2>
              <p className="text-sm text-gray-600 mb-4 sm:mb-6">
                Update your password to keep your account secure
              </p>

              <form onSubmit={handlePasswordChange} className="space-y-4 sm:space-y-5">
                {/* Current Password */}
                <div className="space-y-2">
                  <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-900">
                    Current Password <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type={showPasswords.current ? "text" : "password"}
                      id="currentPassword"
                      value={passwordData.currentPassword}
                      onChange={(e) => handlePasswordInputChange('currentPassword', e.target.value)}
                      className="w-full pl-10 pr-12 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Enter current password"
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility('current')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPasswords.current ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {/* New Password */}
                <div className="space-y-2">
                  <label htmlFor="newPassword" className="block text-sm font-medium text-gray-900">
                    New Password <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type={showPasswords.new ? "text" : "password"}
                      id="newPassword"
                      value={passwordData.newPassword}
                      onChange={(e) => handlePasswordInputChange('newPassword', e.target.value)}
                      className="w-full pl-10 pr-12 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Enter new password (min. 8 characters)"
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility('new')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPasswords.new ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {/* Confirm New Password */}
                <div className="space-y-2">
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-900">
                    Confirm New Password <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type={showPasswords.confirm ? "text" : "password"}
                      id="confirmPassword"
                      value={passwordData.confirmPassword}
                      onChange={(e) => handlePasswordInputChange('confirmPassword', e.target.value)}
                      className="w-full pl-10 pr-12 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Confirm new password"
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility('confirm')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPasswords.confirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={changingPassword}
                  className="w-full px-6 py-3 text-white rounded-full font-bold text-sm uppercase tracking-wide shadow-sm hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  style={{ background: 'var(--pmc-primary)' }}
                >
                  {changingPassword ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Updating Password...</span>
                    </>
                  ) : (
                    <>
                      <Lock className="w-5 h-5" />
                      <span>Update Password</span>
                    </>
                  )}
                </button>
              </form>
            </div>
          )}

          </div>
        </div>
      </div>

    </div>
  );
};

export default Profile;
